import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  ApiService,
  Order,
  Ticket,
  TicketWalletBalanceLeg,
} from '@hypenation/tickets';
import { Microservice } from '@hypenation/shared';
import { AuthStateService } from '@hypenation/auth';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { ShareTicketModalComponent } from '../../components/share-ticket-modal/share-ticket-modal.component';
import { TicketDetailModalComponent } from '../../components/ticket-detail-modal/ticket-detail-modal.component';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { environment } from '@env/environment';

// Defined once, in MyTicketsLoaderService, so the events section and this screen agree on the
// shape. Re-exporting it here too made the library index ambiguous.
import type { TicketWithOrder } from '../../services/my-tickets-loader.service';

@Component({
  selector: 'hypenation-my-tickets',
  standalone: true,
  imports: [
    CommonModule,
    NzCardModule,
    NzTagModule,
    NzSpinModule,
    NzEmptyModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    RouterModule,
    ShareTicketModalComponent,
  ],
  templateUrl: './my-tickets.component.html',
  styleUrl: './my-tickets.component.css',
})
export class MyTicketsComponent implements OnInit {
  loading = false;
  tickets: TicketWithOrder[] = [];
  userEmail: string | null = null;

  /** Wallet balance legs per ticket uid, for tickets on wallet-enabled events. */
  walletBalances = new Map<string, TicketWalletBalanceLeg[]>();

  /** Ticket uids whose wallet lookup succeeded, i.e. a wallet exists — see {@link hasWallet}. */
  private ticketsWithAWallet = new Set<string>();

  /**
   * Set only when this component is serving `/my-events/:eventUid`, where it shows one event's
   * tickets. Empty on `/tickets`, which stays the flat list of everything.
   *
   * Reusing this screen rather than building a second ticket grid keeps one implementation of the
   * card, its wallet balance and its top-up gating.
   */
  eventUid = '';

  private readonly apiService = inject(ApiService);
  private readonly authStateService = inject(AuthStateService);
  private readonly modalService = inject(NzModalService);
  private readonly route = inject(ActivatedRoute);

  ngOnInit(): void {
    this.userEmail = this.authStateService.getUserEmail();

    this.route.paramMap.subscribe((params) => {
      this.eventUid = params.get('eventUid') ?? '';
    });

    if (this.userEmail) {
      this.loadAllTickets();
    }
  }

  /** Every ticket, or one event's worth when opened from My Events. */
  get visibleTickets(): TicketWithOrder[] {
    if (!this.eventUid) return this.tickets;
    return this.tickets.filter((t) => t.event?.uid === this.eventUid);
  }

  /** The event being viewed, for the header. Null on the flat list. */
  get viewedEvent(): TicketWithOrder['event'] | null {
    if (!this.eventUid) return null;
    return this.visibleTickets[0]?.event ?? null;
  }

  /**
   * What is on this event's wallets, per currency. Only ever one event's — funds on a ticket are
   * spendable at that event and nowhere else, so a combined total would be unusable.
   */
  get viewedEventWalletTotals(): { currencyCode: string; balance: number }[] {
    if (!this.eventUid) return [];
    const totals = new Map<string, number>();
    for (const ticket of this.visibleTickets) {
      for (const leg of this.walletBalanceFor(ticket.uid)) {
        totals.set(leg.currencyCode, (totals.get(leg.currencyCode) ?? 0) + Number(leg.balance ?? 0));
      }
    }
    return [...totals.entries()].map(([currencyCode, balance]) => ({ currencyCode, balance }));
  }

  loadAllTickets(): void {
    if (!this.userEmail) return;
    this.loading = true;

    // Fetch all orders first, then fetch tickets for each completed order
    this.apiService.getAllMyOrdersByEmail(this.userEmail).pipe(
      switchMap(res => {
        const completedOrders = (res.body ?? []).filter(o => o.status === 'COMPLETED');
        if (completedOrders.length === 0) return of([] as TicketWithOrder[]);

        const ticketRequests = completedOrders.map(order =>
          this.apiService.getTicketByOrderNumber(order.orderNumber).pipe(
            map(r => (r.body ?? []).map(t => ({ ...t, orderNumber: order.orderNumber } as TicketWithOrder)))
          )
        );

        return forkJoin(ticketRequests).pipe(
          map(responses => responses.flat())
        );
      })
    ).subscribe({
      next: (allTickets) => {
        this.tickets = allTickets;
        this.loading = false;
        this.loadWalletBalances(allTickets);
      },
      error: () => (this.loading = false),
    });
  }

  /**
   * Balances for tickets on wallet-enabled events (QA finding GAP-1 — a customer could fund a
   * wallet and then never see what was on it).
   *
   * Loaded after the tickets render rather than blocking them: a balance is useful but not worth
   * delaying the list for. A per-ticket failure is swallowed on purpose — a ticket on a
   * wallets-enabled event legitimately has no wallet when it was created before the flag was turned
   * on, and that is not something to alarm a customer about.
   */
  private loadWalletBalances(tickets: TicketWithOrder[]): void {
    const walletTickets = tickets.filter((t) => t.event?.walletsEnabled);
    if (!walletTickets.length) return;

    forkJoin(
      walletTickets.map((ticket) =>
        this.apiService.getMyTicketWalletBalance(ticket.uid).pipe(
          map((res) => ({
            uid: ticket.uid,
            exists: true,
            balances: res.body?.balances ?? [],
          })),
          catchError(() =>
            of({
              uid: ticket.uid,
              exists: false,
              balances: [] as TicketWalletBalanceLeg[],
            })
          )
        )
      )
    ).subscribe((results) => {
      results.forEach((result) => {
        this.walletBalances.set(result.uid, result.balances);
        if (result.exists) this.ticketsWithAWallet.add(result.uid);
      });
    });
  }

  /** Non-empty only when this ticket has a wallet holding at least one currency. */
  walletBalanceFor(ticketUid: string): TicketWalletBalanceLeg[] {
    return this.walletBalances.get(ticketUid) ?? [];
  }

  /**
   * Whether the ticket actually has a wallet — which is not the same as its event having wallets
   * enabled. A ticket issued before the flag was turned on has no wallet (spec-conformant), so
   * gating the top-up on `walletsEnabled` alone offered an action that could only fail (QA finding
   * NEW-6). An empty balance list cannot stand in for this: a real wallet holding nothing looks
   * identical to no wallet at all.
   */
  hasWallet(ticketUid: string): boolean {
    return this.ticketsWithAWallet.has(ticketUid);
  }

  getStatusColor(status: string | undefined): string {
    if (!status) return 'default';
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'USED':
        return 'blue';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  }

  getTicketDownloadUrl(ticketUid: string): string {
    return `${environment.apiUrl}/${Microservice.ETICKETING}/opn/v2/download-tickets?uid=${ticketUid}`;
  }

  /**
   * Ticket details for a customer who tapped the card. Opened from the card body only — the footer
   * controls stop propagation, so tapping Share, Download, Top Up or Order does what it says rather
   * than also raising this.
   */
  openTicketDetail(ticket: TicketWithOrder): void {
    this.modalService.create({
      nzTitle: 'Ticket details',
      nzContent: TicketDetailModalComponent,
      nzData: {
        ticket,
        balances: this.walletBalanceFor(ticket.uid),
        hasWallet: this.hasWallet(ticket.uid),
        downloadUrl: this.getTicketDownloadUrl(ticket.uid),
      },
      nzFooter: null,
      nzWidth: 460,
    });
  }

  openShareModal(ticketUid: string): void {
    this.modalService.create({
      nzTitle: 'Share Ticket',
      nzContent: ShareTicketModalComponent,
      nzData: { ticketUid },
      nzFooter: null,
      nzWidth: 450
    });
  }
}
